import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { FormikTimePicker } from 'src/components/shared/form/formik/FormikTimePicker'
import type { PaymentReleaseScheduleFormValues } from 'src/interfaces/invoice'
import {
  formatInstantInLocalZone,
  getLatestReleaseWallClock,
  isLocalZoneCentral,
  toCentralInstant,
  toCentralWallClock,
} from 'src/utils/paymentReleaseTime'
import {
  isValidPickerValue,
  pickerValueFromWallClock,
  wallClockFromPicker,
} from './pickerWallClock'

/**
 * Both pickers write the SAME field, the pattern `RescheduleJobPanel` established: each MUI X
 * picker keeps the parts of the value it does not edit, so the pair yields one wall clock.
 *
 * The day bounds are Central Time days, not the browser's — late evening in Lisbon is still
 * "today" in Chicago. They are fixed at mount and only grey out whole days; the validator owns
 * the exact rule, against the clock at submit time.
 */
const PaymentReleaseScheduleFieldsComponent = () => {
  const { values } = useFormikContext<PaymentReleaseScheduleFormValues>()

  const { minDate, maxDate } = useMemo(() => {
    return {
      minDate: pickerValueFromWallClock(toCentralWallClock()),
      maxDate: pickerValueFromWallClock(getLatestReleaseWallClock()),
    }
  }, [])

  const showsLocalTime = useMemo(() => {
    return !isLocalZoneCentral()
  }, [])

  // What the pick resolves to. In the skipped spring-forward hour this already shows the
  // shifted time, which is exactly what the server will store.
  const resolvedInstant = useMemo(() => {
    if (!isValidPickerValue(values.releaseAt)) {
      return null
    }

    const instant = toCentralInstant(wallClockFromPicker(values.releaseAt))

    return instant.isValid ? instant : null
  }, [values.releaseAt])

  return (
    <div css={tw`flex flex-col gap-3`}>
      <Typography variant="body2" css={tw`text-shades-500`}>
        Pick the time in Central Time (America/Chicago), wherever you are.
      </Typography>
      <div css={tw`flex flex-row gap-3`}>
        <FormikDatePicker<PaymentReleaseScheduleFormValues>
          name="releaseAt"
          label="Date (CT)"
          minDate={minDate}
          maxDate={maxDate}
          validateOnPick
          fullWidth
        />
        <FormikTimePicker<PaymentReleaseScheduleFormValues> name="releaseAt" label="Time (CT)" />
      </div>
      {resolvedInstant ? (
        <div
          css={tw`flex flex-col gap-1 rounded-lg border border-solid border-shades-100 bg-shades-50 p-3`}
          data-testid="payment-release-schedule-preview"
        >
          <Typography variant="subtitle2">
            Releases {resolvedInstant.toFormat('LLL d, yyyy, h:mm a ZZZZ')}
          </Typography>
          {showsLocalTime ? (
            <Typography variant="body2" css={tw`text-shades-500`}>
              That is {formatInstantInLocalZone(resolvedInstant)} your time.
            </Typography>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export const PaymentReleaseScheduleFields = memo(PaymentReleaseScheduleFieldsComponent)
